import { useSelector } from 'react-redux';
import { CartoLayer } from '@deck.gl/carto';
import { selectSourceById } from '@carto/react-redux';
import { useCartoLayerProps } from '@carto/react-api';
import {
  getLineColorFunctionMapping,
  getLineWidthFunctionMapping,
  getFillColorFunctionMapping,
  getPointRadiusFunctionMapping,
} from 'components/contexts/LayerStyleReferenceMapping';
import { useAppState } from 'components/contexts/State';
import {
  getFormattedValue,
  getNameFormatterFromColumn,
  getValueFormatterFromColumn,
} from 'components/contexts/ColumnFormatterMapping';
import getPinnedTooltipCoordinate from 'utils/getPinnedTooltipCoordinate';

export const CUSTOMSQL_SECONDARY_LAYER_ID = 'customSqlSecondaryLayer';

function CustomSqlSecondaryLayer() {
  const { customSqlSecondaryLayer } = useSelector((state) => state.carto.layers);
  const source = useSelector((state) =>
    selectSourceById(state, customSqlSecondaryLayer?.source)
  );
  const cartoLayerProps = useCartoLayerProps({ source });
  const viewState = useSelector((state) => state.carto.viewState);

  const { customSqlData, pinnedTooltip, setPinnedTooltip } = useAppState();

  if (customSqlSecondaryLayer && source) {
    return new CartoLayer({
      ...cartoLayerProps,
      id: CUSTOMSQL_SECONDARY_LAYER_ID,

      stroked: false,
      pointRadiusUnits: 'pixels',
      lineWidthUnits: 'pixels',
      pickable: true,
      visible: customSqlSecondaryLayer.visible,
      opacity: customSqlSecondaryLayer.opacity,

      getFillColor: getFillColorFunctionMapping[customSqlSecondaryLayer.getFillColor]
        ? getFillColorFunctionMapping[customSqlSecondaryLayer.getFillColor]
        : [0, 0, 0],
      pointRadiusMinPixels: customSqlSecondaryLayer.pointRadiusMinPixels
        ? customSqlSecondaryLayer.pointRadiusMinPixels
        : 1,
      getPointRadius: (v) => {
        if (
          customSqlSecondaryLayer.getPointRadius === 'default_point'
        )
          return getPointRadiusFunctionMapping.default_point(v);
        if (
          customSqlSecondaryLayer.getPointRadius === 'secondary_point'
        )
          return getPointRadiusFunctionMapping.secondary_point(v, viewState.zoom);
        return 5;
      },

      getLineColor: getLineColorFunctionMapping[customSqlSecondaryLayer.getLineColor]
        ? getLineColorFunctionMapping[customSqlSecondaryLayer.getLineColor]
        : [0, 0, 0],
      lineWidthMinPixels: customSqlSecondaryLayer.lineWidthMinPixels
        ? customSqlSecondaryLayer.lineWidthMinPixels
        : 1,
      getLineWidth: (v) => {
        if (
          customSqlSecondaryLayer.getLineWidth === 'default_line'
        )
          return getLineWidthFunctionMapping.default_line(v);
        if (
          customSqlSecondaryLayer.getLineWidth === 'secondary_line'
        )
          return getLineWidthFunctionMapping.secondary_line(v, viewState.zoom);
        return 4;
      },

      pointType: customSqlSecondaryLayer.pointType
        ? customSqlSecondaryLayer.pointType
        : 'circle',

      onDataLoad: (data) => {
        cartoLayerProps.onDataLoad && cartoLayerProps.onDataLoad(data);
      },

      onClick: (info) => {
        if (info?.object && info?.object?.properties && customSqlData?.[1]?.properties?.tooltip) {
          const tooltip = customSqlData[1].properties.tooltip;
          const title = tooltip.title ? tooltip.title : 'Details';
          let subTitle = tooltip.subtitle ? tooltip.subtitle : '';
          if (
            tooltip.subtitle_column &&
            info?.object?.properties[tooltip.subtitle_column]
          ) {
            subTitle = info?.object?.properties[tooltip.subtitle_column];
          }
          let htmlString = '';
          htmlString += `<div class="deck-tooltip-title">${title}</div>`;
          if (subTitle) {
            htmlString += `<div class="deck-tooltip-subtitle">${subTitle}</div>`;
          }
          htmlString += `<div class="deck-hr"></div><div class="deck-tooltip-msg-container">`;
          tooltip.columns.forEach((column) => {
            htmlString += `<div class="deck-tooltip-msg-wrapper"><div class="deck-tooltip-msg-text-1">${
              getNameFormatterFromColumn(source?.data, column)
                ? getNameFormatterFromColumn(source?.data, column)
                : column.replace(/_/g, ' ')
            }</div><div class="deck-tooltip-msg-text-2">`;
            htmlString += getValueFormatterFromColumn(source?.data, column)
              ? new Function(
                  'return ' + getValueFormatterFromColumn(source?.data, column)
                )()(info?.object?.properties[column])
              : getFormattedValue(info?.object?.properties[column]);
            htmlString += `</div></div>`;
          });
          htmlString += `</div>`;

          if (pinnedTooltip?.html === htmlString) {
            setPinnedTooltip(null);
          } else {
            let locationType;
            if(title.toLowerCase().includes('intersection')) {
              locationType = 'Intersection';
            } else if (title.toLowerCase().includes('ramp')) {
              locationType = 'Ramp';
            } else if (title.toLowerCase().includes('segment')) {
              locationType = 'Segment';
            } else {
              locationType = null;
            }
            setPinnedTooltip({
              html: htmlString,
              postMileInfo: {
                Postmile: info?.object?.properties?.Postmile,
                Location_Type: locationType,
              },
              coordinate: getPinnedTooltipCoordinate(info),
            });
          }
        }
      },

      onHover: (info) => {
        if (info?.object && info?.object?.properties) {
          const tooltip = customSqlData[1].properties.tooltip;
          const title = tooltip.title ? tooltip.title : 'Details';
          let subTitle = tooltip.subtitle ? tooltip.subtitle : '';
          if (
            tooltip.subtitle_column &&
            info?.object?.properties[tooltip.subtitle_column]
          ) {
            subTitle = info?.object?.properties[tooltip.subtitle_column];
          }
          let htmlString = '';
          htmlString += `<div class="deck-tooltip-title">${title}</div>`;
          if (subTitle) {
            htmlString += `<div class="deck-tooltip-subtitle">${subTitle}</div>`;
          }
          htmlString += `<div class="deck-hr"></div><div class="deck-tooltip-msg-container">`;
          tooltip.columns.forEach((column) => {
            htmlString += `<div class="deck-tooltip-msg-wrapper"><div class="deck-tooltip-msg-text-1">${
              getNameFormatterFromColumn(source?.data, column)
                ? getNameFormatterFromColumn(source?.data, column)
                : column.replace(/_/g, ' ')
            }</div><div class="deck-tooltip-msg-text-2">`;
            htmlString += getValueFormatterFromColumn(source?.data, column)
              ? new Function(
                  'return ' + getValueFormatterFromColumn(source?.data, column)
                )()(info?.object?.properties[column])
              : getFormattedValue(info?.object?.properties[column]);
            htmlString += `</div></div>`;
          });
          htmlString += `</div>`;
          info.object = { html: htmlString };
        }
      },
    });
  }
}

export default CustomSqlSecondaryLayer;
